import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { DxPivotGridComponent } from 'devextreme-angular';
import { SuspectMap } from '../types/SuspectMap';
import { LocationSnapshotFilters } from '../types/LocationSnapshotFilters';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import * as chroma from "chroma-js";
import * as wkt from 'wkt';
import * as geojson from "geojson";
import * as bbox from "geojson-bbox";
import { SharedService } from 'src/app/services/SharedService';
import { MapLegendsData } from '../types/MapLegendsData';

@Component({
  selector: 'suspect-heat-map',
  templateUrl: './suspect-heat-map.component.html',
  styleUrls: ['./suspect-heat-map.component.scss']
})
export class SuspectHeatMapComponent implements OnInit, AfterViewInit, OnChanges {

  @Input()
  suspectMap: SuspectMap;

  @Input()
  filters: LocationSnapshotFilters;

  @Output() export = new EventEmitter();

  mapConfigs: Array<{mapName: string, displayName: string, viewportDivID: string, legendDivID: string}> = [
    {mapName: "MVA", displayName: '', 
      viewportDivID: "suspects-heatmap1-viewport", legendDivID: "suspects-heatmap1-legend"},
    {mapName: "MVA_NoAP", displayName: 'Predicted Volume Without AP Accounts', 
      viewportDivID: "suspects-heatmap2-viewport", legendDivID: "suspects-heatmap2-legend"},
  ];

  loader: Loader;
  google: any;
  heatMap1: google.maps.Map;
  heatMap2: google.maps.Map;
  bounds: any;
  infoWindow: google.maps.InfoWindow; 

  showInfoText = false;
  hideLegend1 = true;
  hideLegend2 = true;

  header1_line1: string = '';
  header2: string = '';
  header3_line1: string = '';
  header3_line2: string = '';
  header4: string = '';
  header5: string = '';
  mapLegendsData:MapLegendsData=null;

  all_headers: any;

  
  constructor(private sharedSvc: SharedService) {

  }


  ngOnInit(): void {

  }

  
  ngAfterViewInit(): void {

    //This loads the google javascript API once.
    new Loader({
      apiKey: environment.gmaps,
      version: 'weekly'
    }).load().then((google) => {
      this.google = google;
    });
    
  }

  ngOnChanges(changes: SimpleChanges): void {  

    //Set headers if required data is received.
    if(this.filters.siteNames && this.filters.globalSegments && this.filters.radiusAroundEachPlant){
      this.setHeaders();
    }

    let mapViewport1 = document.getElementById("suspects-heatmap1-viewport");
    let mapViewport2 = document.getElementById("suspects-heatmap2-viewport");

    //The application-data contains 5 main data arrays. 
    //Paint the map and populate datagrid if there is data in any of these arrays.
    if(this.suspectMap.heatMap1Data.length > 0
      || this.suspectMap.heatMap2Data.length > 0
      || this.suspectMap.apPlantsData.length > 0
      || this.suspectMap.competitorPlantsData.length > 0
      || this.suspectMap.competitorAPPlantsData.length > 0
      || this.suspectMap.proposedPlantsData.length > 0){
      this.showInfoText = false;
      if(mapViewport1) mapViewport1.innerHTML = "";
      if(mapViewport2) mapViewport1.innerHTML = "";
      this.bounds = null;
      this.initMap();      
    }else{ //If main data arrays are empty, just show some info inside map viewport.
      if(mapViewport1) mapViewport1.innerHTML = "<div>No data available. Map cannot be generated.</div>";
      if(mapViewport2) mapViewport2.innerHTML = "<div>No data available. Map cannot be generated.</div>";
      this.showInfoText = true;
    }

  }

  setHeaders(){
    this.header1_line1 = `Volume for Segments: ${this.filters.globalSegments}`;

    this.header2 = "AIR PRODUCTS CONFIDENTIAL - INTERNAL USE ONLY";

    this.header3_line1 = 'Isoline/circles are only used for visual guidance and not used for or representative of any calculations in the reports.';
    this.header3_line2 = 'Green squares are AP plants.  Black dots are competitor plants.  Green diamonds are AP proposed plants. You may mouse-over any of these to get details.';

    this.header4 = 'Predicted Volume Without AP Accounts';
    this.header5 = 'Predicted Volume With AP Accounts'
    this.all_headers = {
      header1_line1: this.header1_line1,
      header2: this.header2,
      header3_line1: this.header3_line1,
      header3_line2: this.header3_line2,
      header4: this.header4,
      header5: this.header5
    };

  }


  initMap() {      
    this.generateMap1();
    this.generateMap2();
  }

  async generateMap1() {

    this.heatMap1 = new this.google.maps.Map(document.getElementById("suspects-heatmap1-viewport") as HTMLElement, {
      zoom: 6,
      mapTypeId: "terrain",
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,      
      disableDoubleClickZoom: true,
      mapId: environment.mapId
    });    

    await this.plotHeatMapTiles(this.heatMap1, this.suspectMap.heatMap1Data, "suspects-heatmap1-legend",true);

    this.hideLegend1 = false;

    await this.plotCompetitorSites(this.heatMap1);

    await this.plotAPPlantSites(this.heatMap1);

    await this.plotCompetitorAPSites(this.heatMap1);

    await this.plotBullsEye(this.heatMap1);

    if(this.sharedSvc.UserRoles.isIGPower){
      await this.plotProposedPlantSites(this.heatMap1);
    }

    this.setFeatureStyles(this.heatMap1);

    this.adjustSuspectsMapViewport();

  }

  async generateMap2() {

    this.heatMap2 = new this.google.maps.Map(document.getElementById("suspects-heatmap2-viewport") as HTMLElement, {
      zoom: 6,
      mapTypeId: "terrain",
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,      
      disableDoubleClickZoom: true,
      mapId: environment.mapId
    });

    await this.plotHeatMapTiles(this.heatMap2, this.suspectMap.heatMap2Data, "suspects-heatmap2-legend", false);

    this.hideLegend2 = false;

    await this.plotCompetitorSites(this.heatMap2);

    await this.plotAPPlantSites(this.heatMap2);

    await this.plotCompetitorAPSites(this.heatMap2);

    await this.plotBullsEye(this.heatMap2);

    if(this.sharedSvc.UserRoles.isIGPower){
      await this.plotProposedPlantSites(this.heatMap2);
    }

    this.setFeatureStyles(this.heatMap2);

  }

  async plotHeatMapTiles(map: google.maps.Map, heatMapData: Array<any>, legendDivID: string, isCalculateLegendBreakPoint: boolean) {

    let mva_mtd_values = heatMapData.map(item => item.custcount);

    //For the MTD values, generate breaks and colors
    if (isCalculateLegendBreakPoint) {
      this.mapLegendsData = this.getBreaksAndColors(mva_mtd_values);
    }

    //Set the Legend
    let mapLegend = document.getElementById(legendDivID) as HTMLElement;
    mapLegend.innerHTML = '';

    mapLegend.innerHTML = `<p style="text-align: center"><b>MVA MT/day</b></p><hr />`;

    if(mva_mtd_values.length > 0){
      this.mapLegendsData.colors.forEach((color,i) => {
        const div = document.createElement("div");
        div.innerHTML = `<svg height="20" width="50">  <rect width="40" height="20" style="stroke-width:3;fill:${color}" /> </svg>` + `${this.mapLegendsData.breaks_legend[i]}`;
        mapLegend.appendChild(div);
      });
    }else{ //i.e if there are no values..

      //Set legend text accordingly.
      const div = document.createElement("div");
      div.innerHTML = "<p>No data to display.</p>";          
      mapLegend.appendChild(div);
      
    }

    //determine the color code based on its MTD value. This function will be used down the line.
    const getColor = (mva_mtd: number): string => {

      if(mva_mtd || mva_mtd == 0){

        if(mva_mtd == 0) return this.mapLegendsData.colors[0];        
        return this.mapLegendsData.colors[this.mapLegendsData.breaks.findIndex(n => n >= mva_mtd)];

      }else{
        return "#FFFFFF";
      }
      
    }

    let geoData: Array<any> = [];

    heatMapData.forEach(item => {
      geoData.push({
        type: "heatmap_tile",
        mvaMTD: item.custcount,        
        fillColor: getColor(item.custcount),
        geo: wkt.parse(item.geog)
      });
    });

    //Parse the geo-data array into GeoJSON and create a Feature Collection object.
    // References -
    // 1. https://www.npmjs.com/package/geojson
    // 2. https://geojson.org/geojson-spec.html#examples
    let featureCollection = geojson.parse(geoData, {GeoJSON: 'geo'});      

    //Generate a bounding box for this feature collection. will not be visible in the map.
    //Use this bounding box to determine the center point and set it as the map view's center.      


    //Add the geo-json to the map. This will generate all the polygons in the map (i.e counties)
    map.data.addGeoJson(featureCollection);    

    const handleTileClick = (event) => {
      
      let feature = event.feature;

      if(feature.getProperty("type") != 'heatmap_tile') return;
      
      let mtd = feature.getProperty("mvaMTD");
      
      if (!mtd && mtd != 0) return;    

      let content = `<span style="font-size:small">${mtd}</span>`;
  
      if(this.infoWindow){
        this.infoWindow.close();
      }
      this.infoWindow = new this.google.maps.InfoWindow();
      this.infoWindow.setContent(content);
      this.infoWindow.setPosition(event.latLng);
      this.infoWindow.open(map);
    }
    
    //Listener for click event, to display info when user clicks on a county.
    map.data.addListener('click', handleTileClick);

  }

  

  async plotAPPlantSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let color = (this.filters.plantCompany == 'Air Products')? 'lime': 'red';

    let apPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                              <rect width="15" height="15" style="fill:${color};stroke-width:2;stroke:rgb(0,0,0)" />
                            </svg>`;
    
    let miles125Array = [];
           
    this.suspectMap.apPlantsData.forEach(apPlant => {

      let point = wkt.parse(apPlant.geoLocation); 

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(apPlantSVGString, 'image/svg+xml').documentElement,
        title: apPlant.plantCoSite
      });

      miles125Array.push({ type: 'miles125', geo: wkt.parse(apPlant.miles125) });

    });
    
    // Looks like creating a extra ring on Maps
    //let featureCollection = geojson.parse(miles125Array, {GeoJSON: 'geo'});

    //map.data.addGeoJson(featureCollection); 

  }

  async plotProposedPlantSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let proposedPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                                    <rect width="10" height="10" x="10" y="-4" transform="rotate(45)" 
                                      style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                                  </svg>`;    

    this.suspectMap.proposedPlantsData.forEach(proposedPlant => {

      let point = wkt.parse(proposedPlant.geoLocation); 

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(proposedPlantSVGString, 'image/svg+xml').documentElement,
        title: proposedPlant.plantCoSite
      });

    });

  }

  async plotCompetitorSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let compSVGString = '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10">  <circle cx="5" cy="5" r="4" stroke="black" stroke-width="0.5" fill="black" /> </svg>';    


    this.suspectMap.competitorPlantsData.forEach(compPlant => {

      let point = wkt.parse(compPlant.geoLocation); 
            
      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(compSVGString, 'image/svg+xml').documentElement,
        title: compPlant.plantCoSite
      });    

    });

  }

  async plotCompetitorAPSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();    
    
    let compAP_SVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                              <rect width="9" height="9" x="3" y="3" 
                                style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                            </svg>`;

    this.suspectMap.competitorAPPlantsData.forEach(compAPPlant => {

      let point = wkt.parse(compAPPlant.geoLocation); 
            
      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(compAP_SVGString, 'image/svg+xml').documentElement,
        title: compAPPlant.plantCoSite
      });
            
    });

  }

  async plotBullsEye(map: google.maps.Map){

    //Add rings
    let ringArray = [];
    this.suspectMap.bullsEyeData.forEach(item => {
      ringArray.push({ type: 'bullsEye', geo: wkt.parse(item.bullseye) });      
    });

    let featureCollection = geojson.parse(ringArray, {GeoJSON: 'geo'});

    if(!this.bounds){
      let boundingBox = bbox(featureCollection);
      this.bounds = new google.maps.LatLngBounds();      
      this.bounds.extend({lat: boundingBox[1], lng: boundingBox[0]});
      this.bounds.extend({lat: boundingBox[3], lng: boundingBox[2]});
    }
    
    //this.adjustSuspectsMapViewport();

    map.data.addGeoJson(featureCollection);

    //Add distance labels to the rings
    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");
    const parser = new DOMParser();

    this.suspectMap.bullsEyeData.forEach(item => {

      let point = wkt.parse(item.geoPoint);

      let svgText = `<svg xmlns="http://www.w3.org/2000/svg" height="30" width="60">
                        <text x="5" y="28" font-size="12" 
                          font-weight="bold" fill="black">${item.dist} miles</text>
                      </svg>`;

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(svgText, 'image/svg+xml').documentElement,
      });

    });

  }

  adjustSuspectsMapViewport(){
    this.heatMap1.fitBounds(this.bounds);
    this.heatMap1.setCenter(this.bounds.getCenter());

    this.heatMap2.fitBounds(this.bounds);
    this.heatMap2.setCenter(this.bounds.getCenter());
  }

  setFeatureStyles(map: google.maps.Map) {

    let heatMapStyle = {
      strokeColor: "#808080",
      strokeOpacity: 1.0,
      strokeWeight: 0.5,
      fillOpacity: 0.7,
      zIndex: 1     
    }
    
    let bullsEyeStyle = {
      strokeColor: "#4d4d4d",
      strokeOpacity: 1.0,
      strokeWeight: 2,        
      fillOpacity: 0.0,   
      zIndex: 10   
    };

    let miles125Style = {
      strokeColor: "#4d4d4d",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillOpacity: 0.0,
      zIndex: 10   
    };

    map.data.setStyle(function(feature) {    
      
      let type = feature.getProperty('type');

      if(type == 'heatmap_tile') {
        heatMapStyle['fillColor'] = feature.getProperty("fillColor");
        return heatMapStyle;
      }

      if(type == 'bullsEye') return bullsEyeStyle;

      if(type == 'miles125') return miles125Style;

    });

  }

  getBreaksAndColors(values: number[]): MapLegendsData {
    //the input 'values' is an array of numbers using which the map will be color coded; Ex MTD values.
    //For the given 'values', we will generate the breaks and colors array.

    // Ex: If MTD Values = [0.39,0.76,1.52,1.62,2.11,2.48,16.22,19.71]
    // Then, breaks = [1,4,7,11,14,17,20]
    // And, breaks_legend = ["0-1","1-4","4-7","7-11","11-14","14-17","17-20"]
    // And, colors = ["#008000","#80c000","#ffff00","#ffd200","#ffa500","#ff5200","#ff0000"]

    //sort input and remove dupes
    let valuesUniq = Array.from(new Set(values.sort((a,b) => a-b)));

    //n is the number of breaks to be generated; maximum is 5
    let n = (valuesUniq.length >= 5)? 5 : valuesUniq.length;

    let breaks = [];
    let colors = [];
    let breaks_legend = [];

    if(n != 0){
      //generate 'n' equidistant breaks and round them up. Refer to https://gka.github.io/chroma.js/#chroma-limits
      breaks = Array.from(new Set(chroma.limits(valuesUniq, 'e', n-1)
                                    .sort((a,b) => a-b)
                                    .map(mtd => Math.ceil(mtd))));

      //In some cases where MTD value is 0
      if(breaks[0] == 0){
        //If 0 is the only value we replace it with 1, so that legend shows as 0-1. otherwise it will show as 0-0 (wrong).
        if(breaks.length == 1) {
          breaks[0] = 1;
        } else{ //there are other values, just remove zero in the first position and legends will be generated fine.
          breaks = breaks.slice(1);
        }
        
      }

      let colorBands = [];
      if(breaks.length <= 2){
        colorBands = ['green','yellow'];
      }else if(breaks.length <= 4){
        colorBands = ['green','yellow','orange'];
      }else {
        colorBands = ['green','yellow','orange', 'red'];
      }
      //generate equidistant colors. Refer to https://gka.github.io/chroma.js/#color-scales
      colors = chroma.scale(colorBands).colors(breaks.length);

      //generate the breaks legend labels
      breaks.forEach((val,i) => {
        if(i == 0 && breaks[0] != 0) {breaks_legend.push(`0 - ${val}`)}
        else {breaks_legend.push(`${breaks[i-1]} - ${breaks[i]}`)}
      });
    }

    return {breaks: breaks, breaks_legend: breaks_legend, colors: colors};
  }

  exportExcel($event){
    this.export.emit();
  }

}